import React, { useState, useCallback, useRef, useEffect } from "react";
import { FiThumbsUp, FiThumbsDown, FiCopy, FiLoader, FiStopCircle, FiVolume2 } from "react-icons/fi";
import Tooltip from "../Tooltip";
import { sendMessageFeedback } from "../../utils/assistantFeedbackApi";
import { AI_ASSISTANT_PENDO_EVENTS, trackAssistantEvent } from "../../utils/assistantAnalytics";
import "./MessageActions.scss";

const SPEECH_STATUS = {
  IDLE: "idle",
  LOADING: "loading",
  PLAYING: "playing",
};

/**
 * Inline actions for AI message bubbles: thumbs up/down feedback and copy.
 * Shown only for assistant messages. Copy uses copyLink when present, else copyText.
 * Feedback is sent only when traceId is present (from SSE end event); otherwise no-op.
 */
const MessageActions = ({
  traceId = null,
  copyText = "",
  copyLink,
  initialRating = null,
  onFeedbackChange,
  onCopy,
  onSpeak,
  isSpeakEnabled = true,
  speechStatus: controlledSpeechStatus = null,
  onStopSpeech,
  className = "",
}) => {
  const [rating, setRating] = useState(initialRating ?? null);
  const [speechStatus, setSpeechStatus] = useState(SPEECH_STATUS.IDLE);
  const speechAbortRef = useRef(null);
  const activeSpeechStatus = controlledSpeechStatus || speechStatus;

  const sendFeedback = useCallback(
    (newRating) => {
      if (newRating === null) return;
      const value = newRating === "up" ? 1 : 0;
      sendMessageFeedback({ traceId: traceId || undefined, value, comment: "" });
    },
    [traceId],
  );

  const handleThumbsUp = useCallback(() => {
    const next = rating === "up" ? null : "up";
    setRating(next);
    sendFeedback(next);
    if (next) {
      trackAssistantEvent(AI_ASSISTANT_PENDO_EVENTS.FEEDBACK_SUBMITTED, {
        feedback: "like",
      });
    }
    onFeedbackChange?.(next);
  }, [rating, sendFeedback, onFeedbackChange]);

  const handleThumbsDown = useCallback(() => {
    const next = rating === "down" ? null : "down";
    setRating(next);
    sendFeedback(next);
    if (next) {
      trackAssistantEvent(AI_ASSISTANT_PENDO_EVENTS.FEEDBACK_SUBMITTED, {
        feedback: "dislike",
      });
    }
    onFeedbackChange?.(next);
  }, [rating, sendFeedback, onFeedbackChange]);

  const [showCopied, setShowCopied] = useState(false);
  const copyFeedbackTimerRef = useRef(null);

  useEffect(() => () => {
    if (copyFeedbackTimerRef.current) clearTimeout(copyFeedbackTimerRef.current);
    speechAbortRef.current?.abort();
  }, []);

  const handleCopy = useCallback(async () => {
    const toCopy = copyLink ?? copyText;
    if (!toCopy || !onCopy) return;
    if (copyFeedbackTimerRef.current) {
      clearTimeout(copyFeedbackTimerRef.current);
      copyFeedbackTimerRef.current = null;
    }
    const result = onCopy(toCopy);
    const ok = typeof result?.then === "function" ? await result : result;
    if (ok) {
      trackAssistantEvent(
        copyLink
          ? AI_ASSISTANT_PENDO_EVENTS.LINK_COPIED
          : AI_ASSISTANT_PENDO_EVENTS.COPY_CLICKED,
        {
          copyType: copyLink ? "link" : "message",
        },
      );
      setShowCopied(true);
      copyFeedbackTimerRef.current = setTimeout(() => setShowCopied(false), 2000);
    }
  }, [copyLink, copyText, onCopy]);

  const canCopy = Boolean(copyLink || copyText);
  const copyLabel = showCopied ? "Copied" : (copyLink ? "Copy link" : "Copy message");
  const canSpeak = Boolean(isSpeakEnabled && copyText && onSpeak);
  const isSpeechActive = activeSpeechStatus !== SPEECH_STATUS.IDLE;
  const speakLabel =
    activeSpeechStatus === SPEECH_STATUS.LOADING
      ? "Preparing audio"
      : activeSpeechStatus === SPEECH_STATUS.PLAYING
        ? "Stop audio"
        : "Listen to message";
  const SpeakIcon =
    activeSpeechStatus === SPEECH_STATUS.LOADING
      ? FiLoader
      : activeSpeechStatus === SPEECH_STATUS.PLAYING
        ? FiStopCircle
        : FiVolume2;

  const handleSpeak = useCallback(async () => {
    if (!canSpeak) return;

    if (isSpeechActive) {
      speechAbortRef.current?.abort();
      speechAbortRef.current = null;
      onStopSpeech?.();
      setSpeechStatus(SPEECH_STATUS.IDLE);
      return;
    }

    const abortController = new AbortController();
    speechAbortRef.current = abortController;
    setSpeechStatus(SPEECH_STATUS.LOADING);

    try {
      await onSpeak(copyText, {
        signal: abortController.signal,
        onStateChange: setSpeechStatus,
      });
    } catch (error) {
      if (!abortController.signal.aborted) {
        console.warn("[AI Assistant] Voice playback failed", error);
      }
    } finally {
      if (speechAbortRef.current === abortController) {
        speechAbortRef.current = null;
        setSpeechStatus(SPEECH_STATUS.IDLE);
      }
    }
  }, [canSpeak, copyText, isSpeechActive, onSpeak, onStopSpeech]);

  return (
    <div className={`message-actions ${className}`.trim()} role="group" aria-label="Message actions">
      <Tooltip content="Mark as helpful" placement="top" variant="light">
        <button
          type="button"
          className={`message-actions__btn message-actions__btn--thumbs ${rating === "up" ? "message-actions__btn--active" : ""}`}
          onClick={handleThumbsUp}
          aria-label="Mark as helpful"
          aria-pressed={rating === "up"}
        >
          <FiThumbsUp aria-hidden />
        </button>
      </Tooltip>
      <Tooltip content="Mark as not helpful" placement="top" variant="light">
        <button
          type="button"
          className={`message-actions__btn message-actions__btn--thumbs ${rating === "down" ? "message-actions__btn--active" : ""}`}
          onClick={handleThumbsDown}
          aria-label="Mark as not helpful"
          aria-pressed={rating === "down"}
        >
          <FiThumbsDown aria-hidden />
        </button>
      </Tooltip>
      {canCopy && (
        <Tooltip content={copyLabel} placement="top" variant="light">
          <button
            type="button"
            className="message-actions__btn message-actions__btn--copy"
            onClick={handleCopy}
            aria-label={copyLabel}
          >
            <FiCopy aria-hidden />
          </button>
        </Tooltip>
      )}
      {canSpeak && (
        <Tooltip content={speakLabel} placement="top" variant="light">
          <button
            type="button"
            className={`message-actions__btn message-actions__btn--speak ${isSpeechActive ? "message-actions__btn--active" : ""}`}
            onClick={handleSpeak}
            aria-label={speakLabel}
            aria-busy={activeSpeechStatus === SPEECH_STATUS.LOADING}
          >
            <SpeakIcon
              className={activeSpeechStatus === SPEECH_STATUS.LOADING ? "message-actions__spinner" : ""}
              aria-hidden
            />
          </button>
        </Tooltip>
      )}
    </div>
  );
};

export default MessageActions;
